/**
 * Sky Isles: D major pentatonic. Warm drifting pad (filter breathing on a slow LFO), kalimba
 * melody with glass-bell answers, and — while running — a pillowy groove (bloom kick, shaker,
 * airy clap, soft round bass) plus a kalimba arpeggio at full flow.
 */
import { FadeRoute } from '../bus';
import { bloomKick, shaker, softClap } from '../instruments/drums';
import { glassBell, kalimba } from '../instruments/mallets';
import { padNote, softBass } from '../instruments/pads';
import type { Synth } from '../instruments/synth';
import { degreeMidi, type ThemeDef } from '../themes';
import { disconnect, stopSource, type Rng } from '../util';
import type { BarInfo, Composer, MusicCore } from './composer';
import { Harmony, MelodyLine, MelodyWalker, type Chord } from './melody';
import { pats } from './patterns';

const CHORDS: readonly Chord[] = [
  { name: 'Dadd9', bass: 50, pad: [57, 62, 64, 66], tones: [0, 1, 2, 3], rest: [0, 3] },
  { name: 'Bm11', bass: 47, pad: [54, 57, 62, 64], tones: [4, 0, 2, 3], rest: [4, 2] },
  { name: 'Gmaj7', bass: 43, pad: [55, 59, 62, 66], tones: [4, 0, 2], rest: [0, 4] },
  { name: 'A6sus', bass: 45, pad: [57, 59, 62, 64], tones: [3, 4, 0, 1], rest: [3, 1] },
  { name: 'Em9', bass: 52, pad: [55, 59, 62, 66], tones: [1, 4, 0, 2], rest: [1, 4] },
];

const PROGS: readonly (readonly number[])[] = [
  [0, 1, 2, 3],
  [2, 0, 1, 3],
  [0, 4, 2, 3],
  [1, 2, 0, 3],
  [0, 2, 4, 3],
];

// Indexed by percussion level 0..4.
const KICK = pats(['', 'x.......x.......', 'x.......x.......', 'x.....x.x.......', 'x.....x.x.....x.']);
const SHAKE = pats(['', '....x.......x...', '..x...x...x...x.', 'x.X.x.X.x.X.x.X.', 'xxXxxxXxxxXxxxXx']);
const CLAP = pats(['', '', '....x.......x...', '....x.......x...', '....x.......x..x']);
const BASS = pats(['', '', 'x.........x.....', 'x.....x...x.....', 'x.....x...x...x.']);

const BARS_PER_CHORD = 2;

export class SkyComposer implements Composer {
  readonly route: FadeRoute;
  private readonly s: Synth;
  private readonly pad: Synth;
  private readonly padIn: GainNode;
  private readonly padLp: BiquadFilterNode;
  private readonly padSend: GainNode;
  private readonly lfo: OscillatorNode;
  private readonly lfoAmt: GainNode;
  private readonly rng: Rng;
  private readonly harmony: Harmony;
  private readonly line: MelodyLine;
  private chord: Chord;
  private chordBar = 0;
  private phraseBar = 0;
  private arp = 0;

  constructor(
    core: MusicCore,
    private readonly theme: ThemeDef,
    now: number,
  ) {
    const ctx = core.kit.ctx;
    this.route = new FadeRoute(ctx, core.bus, 0);
    this.s = { kit: core.kit, pool: core.pool, route: this.route };

    // Shared pad bus: all pad notes breathe through one LFO-swept low-pass.
    this.padIn = ctx.createGain();
    this.padLp = ctx.createBiquadFilter();
    this.padLp.type = 'lowpass';
    this.padLp.frequency.value = 1300;
    this.padLp.Q.value = 0.6;
    this.padSend = ctx.createGain();
    this.padSend.gain.value = 0.55;
    this.padIn.connect(this.padLp);
    this.padLp.connect(this.route.dry);
    this.padLp.connect(this.padSend);
    this.padSend.connect(this.route.wet);
    this.lfo = ctx.createOscillator();
    this.lfo.frequency.value = 0.07;
    this.lfoAmt = ctx.createGain();
    this.lfoAmt.gain.value = 420;
    this.lfo.connect(this.lfoAmt);
    this.lfoAmt.connect(this.padLp.frequency);
    this.lfo.start(now);
    this.pad = { kit: core.kit, pool: core.pool, route: { dry: this.padIn, wet: this.padIn } };

    this.rng = core.kit.rng;
    this.harmony = new Harmony(CHORDS, PROGS, this.rng);
    // Melody: F#4 … B5 (degrees 2..9 above D4), starting on D5.
    this.line = new MelodyLine(new MelodyWalker(theme.scale.length, 2, 9, this.rng, 5), this.rng);
    this.chord = CHORDS[0] as Chord;
  }

  bar(b: BarInfo): void {
    const rng = this.rng;
    if (b.restart) {
      this.harmony.restart();
      this.chordBar = 0;
      this.phraseBar = 0;
    }
    if (this.chordBar === 0) {
      this.chord = this.harmony.next();
      const dur = b.barDur * BARS_PER_CHORD;
      const attack = b.playing ? 0.8 : 1.8;
      const pad = this.chord.pad;
      for (let k = 0; k < pad.length; k++) {
        padNote(this.pad, b.t + k * 0.03, pad[k] as number, dur, 0.9, {
          attack,
          release: 2.6,
          wave: this.s.kit.waves.warm,
          detune: 7,
          pan: (k - (pad.length - 1) / 2) * 0.22,
        });
      }
      // Brighten the pad with intensity.
      this.padLp.frequency.setTargetAtTime(1100 + 1000 * b.intensity, b.t, 1.5);
      const top = pad[pad.length - 1] ?? 66;
      if (b.playing && b.intensity > 0.45 && rng.chance(0.5)) {
        glassBell(this.s, b.t, top + 12, 0.14, { send: 0.6, decay: 0.8, pan: rng.bi() * 0.4 });
      } else if (!b.playing && rng.chance(0.3)) {
        // Wind-chime glint in the calm.
        glassBell(this.s, b.t + b.barDur * 0.5, top + 24, 0.08, { send: 0.7, decay: 1.2, pan: rng.bi() * 0.6 });
      }
    }
    this.chordBar = (this.chordBar + 1) % BARS_PER_CHORD;
    this.line.bar(b.density, this.phraseBar, b.playing ? 0.08 : 0.4);
    this.phraseBar = (this.phraseBar + 1) % 4;
  }

  step(i: number, t: number, b: BarInfo): void {
    const rng = this.rng;
    const tt = t + ((i & 1) === 1 ? b.sd * 0.1 : 0); // a breath of swing
    const deg = this.line.step(i, this.chord);
    if (!Number.isNaN(deg)) {
      const final = this.line.isFinal(i);
      const midi = degreeMidi(this.theme, deg, this.theme.melodyRoot);
      const vel = (b.playing ? 0.62 : 0.5) * (i % 8 === 0 ? 1 : 0.82) * rng.range(0.9, 1.05);
      kalimba(this.s, tt + rng.next() * 0.006, midi, vel, {
        send: 0.38,
        pan: rng.bi() * 0.2,
        decay: final ? 1.4 : 1,
      });
      if (final && b.intensity > 0.3 && rng.chance(0.5)) {
        glassBell(this.s, tt, midi + 12, 0.12, { send: 0.6, decay: 0.8 });
      }
    } else if (b.percLevel >= 3 && (i & 3) === 2) {
      const pad = this.chord.pad;
      const n = pad[this.arp % pad.length] ?? 62;
      this.arp++;
      kalimba(this.s, tt, n + 12, 0.15, { send: 0.3, pan: (this.arp & 1) === 1 ? 0.35 : -0.35, decay: 0.55 });
    }

    const L = b.percLevel;
    if (L > 0 && L < KICK.length) {
      if (KICK[L]?.[i]) bloomKick(this.s, tt, i === 0 ? 0.9 : 0.72);
      const sh = SHAKE[L]?.[i] ?? 0;
      if (sh) shaker(this.s, tt, sh === 2 ? 0.8 : 0.42, { pan: 0.28 });
      if (CLAP[L]?.[i]) softClap(this.s, tt, 0.55, { pan: -0.1 });
      if (BASS[L]?.[i]) softBass(this.s, tt, this.chord.bass, b.sd * 6, 0.8);
    }
  }

  dispose(): void {
    stopSource(this.lfo);
    disconnect(this.lfo);
    disconnect(this.lfoAmt);
    disconnect(this.padIn);
    disconnect(this.padLp);
    disconnect(this.padSend);
    this.route.dispose();
  }
}
